import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Select, { GroupedOptionsType, GroupType, ValueType } from "react-select";
import { Typography } from "@material-ui/core";

import {
  eventDefType,
  eventCategories as jsEventCategories,
} from "../../eventTypes";
import { EventDefsState } from "../../reducers/events";
import { StoreState } from "../../store";
import { LoadedEventDefinition } from "../../types/global";

interface SelectEventDefProps {
  /** CSS class names to apply to the select field */
  className?: string;
  /** If `true` the select field is clearable */
  isClearable?: boolean;
  /** If `true` the select field is disabled */
  isDisabled?: boolean;
  /** if `true` the select field is required */
  required?: boolean;
  /** Grouped options to prepend to the available options */
  prependOptions?: Array<GroupType<EventDefOption>>;
  /** The currently selected value */
  value?: ValueType<EventDefOption>;
  /** Called when the selected value is updated */
  onChange: (value: EventDefOption) => void;
  /** Set the placeholder text in the component */
  placeholder?: string;
}

/**
 * A selected event definition option
 */
export interface EventDefOption {
  /** The pretty print label showen in the select field */
  readonly label: string;
  /** The event definition */
  readonly value: LoadedEventDefinition;
}

/**
 * Selects an event definition. Hidden events are excluded
 */
export default (props: SelectEventDefProps) => {
  const { eventDefs: storeEventDefs, loading: eventDefsLoading } = useSelector<
    StoreState,
    EventDefsState
  >((state: StoreState) => state.eventDefs);
  const eventCategories = jsEventCategories as { name: string; type: string }[];

  const [eventDefs, setEventDefs] = useState<
    GroupedOptionsType<EventDefOption>
  >([]);
  useEffect(() => {
    if (eventDefsLoading) return;

    if (storeEventDefs.length === 0) {
      setEventDefs([]);
      return;
    }

    const translatedEventDefs = new Map<string, EventDefOption[]>();
    storeEventDefs.forEach((eventDef) => {
      if (eventDef.is_hidden) return;

      const eventDefCategory = eventDefType(eventDef);
      const source =
        eventCategories.find((category) => category.type === eventDefCategory)
          ?.name ?? "Other";
      const option: EventDefOption = {
        label: eventDef.name,
        value: eventDef,
      };

      const sourceOptions = translatedEventDefs.get(source) ?? [];
      sourceOptions.push(option);
      translatedEventDefs.set(source, sourceOptions);
    });

    const reducedEventDefs = [...(props.prependOptions ?? [])];
    translatedEventDefs.forEach((options, label) => {
      reducedEventDefs.push({ label, options });
    });

    setEventDefs(reducedEventDefs);
  }, [storeEventDefs, eventDefsLoading]);

  const onChange = (value: ValueType<EventDefOption>) => {
    if (value && props.onChange) {
      props.onChange(value as EventDefOption);
    } else {
      props.onChange({} as EventDefOption);
    }
  };

  return (
    <Select<EventDefOption>
      menuPosition="fixed"
      value={props.value}
      onChange={onChange}
      options={eventDefs}
      className={props.className}
      required={props.required}
      isDisabled={props.isDisabled}
      isClearable={props.isClearable}
      isLoading={eventDefsLoading}
      isSearchable={true}
      placeholder={props.placeholder}
      renderValue={(v) => <Typography>v </Typography>}
    />
  );
};
